/**
 * The four numbers that answer "how is the outreach going".
 *
 * WRITTEN TO, ANSWERED, REFUSED, LEFT. In that order, because it is the order a
 * letter passes through: we wrote to this many, this many wrote back, this many
 * never arrived, and this many asked us to stop. Nothing here is a rate on its
 * own - each tile shows a count and puts the share underneath it WITH ITS
 * DENOMINATOR, so "5%" is never asked to stand alone. Five percent of forty is
 * two replies and a coincidence; the tile has to let a reader see that.
 *
 * ⚠ NO "OPENED" AND NO "CLICKED". Neither is tracked in this module, on purpose
 * (docs/crm-turundus-plaan.md, section 10). A tile fed by nothing is exactly the
 * bug the `replied` column was for weeks: a result on screen that could only
 * ever say zero.
 */
import { AlertTriangle, BellOff, Reply, Send } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { DailyPoint, StatusTotals } from "../../../../lib/marketingOverview";
import { Kpi, fmtNum } from "../../../ui";
import { rateText } from "../lib";
import { num } from "../shared";
import { contactedTotal, count, deliveredTotal, rateOf } from "./lib";

/**
 * A share and the number it was taken from, or an admission that there is
 * nothing to take it from yet.
 *
 * The denominator is printed every time rather than only when it is small.
 * There is no threshold at which a percentage stops needing one, and a rule
 * that hides it above some sample size is a rule that has to pick that size.
 */
function RateFoot({ part, whole, ofKey }: { part: number; whole: number; ofKey: string }) {
  const t = useT();
  const rate = rateOf(part, whole);
  if (rate === null) return <>{t("marketing.overview.foot.noBasis")}</>;
  return <>{t(ofKey, { pct: rateText(rate), n: num(whole) })}</>;
}

export function HeadlineCards({ ledger, daily }: { ledger: StatusTotals; daily: DailyPoint[] }) {
  const t = useT();
  const contacted = contactedTotal(ledger);
  const delivered = deliveredTotal(ledger);
  // Only when the window actually holds letters. A sparkline through thirty
  // zeroes is a flat line at the bottom of the tile, and a flat line is a
  // drawing of a measurement - it reads as "steady", not as "nothing yet".
  const series = daily.some((d) => d.sent > 0) ? daily.map((d) => d.sent) : undefined;

  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Kpi
        icon={Send}
        tone="primary"
        label={t("marketing.overview.kpi.contacted")}
        value={fmtNum(contacted)}
        foot={t("marketing.overview.foot.queued", { n: num(count(ledger, "pending")) })}
        series={series}
      />
      <Kpi
        icon={Reply}
        tone="success"
        label={t("marketing.overview.kpi.replied")}
        value={fmtNum(count(ledger, "replied"))}
        // Of DELIVERED, not of contacted. A bounced letter never reached a
        // mailbox, so counting it as an audience for a reply understates every
        // campaign by exactly its worst addresses.
        foot={
          <RateFoot
            part={count(ledger, "replied")}
            whole={delivered}
            ofKey="marketing.overview.foot.ofDelivered"
          />
        }
      />
      <Kpi
        icon={AlertTriangle}
        tone="warning"
        label={t("marketing.overview.kpi.bounced")}
        value={fmtNum(count(ledger, "bounced"))}
        foot={
          <RateFoot
            part={count(ledger, "bounced")}
            whole={contacted}
            ofKey="marketing.overview.foot.ofContacted"
          />
        }
      />
      <Kpi
        icon={BellOff}
        tone="primary2"
        label={t("marketing.overview.kpi.unsubscribed")}
        value={fmtNum(count(ledger, "unsubscribed"))}
        foot={
          <RateFoot
            part={count(ledger, "unsubscribed")}
            whole={delivered}
            ofKey="marketing.overview.foot.ofDelivered"
          />
        }
      />
    </div>
  );
}
